import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Clock, Trash2, Play, RefreshCw, Loader } from 'lucide-react';
import { API_URL } from './config/api';
import authService from './services/authService';

const PAGE_SIZE = 10;
const CACHE_TTL = 60000; // 1 minute
const STALE_TTL = 300000; // 5 minutes - show stale data while revalidating

// Skeleton Card Component for loading state
const SkeletonCard = () => (
  <div className="history-card skeleton-card">
    <div className="thumbnail skeleton-thumbnail">
      <div className="skeleton-shimmer" />
    </div>
    <div className="history-info">
      <div className="skeleton-text skeleton-title" />
      <div className="skeleton-text skeleton-date" />
      <div className="skeleton-actions">
        <div className="skeleton-button" />
        <div className="skeleton-button" />
      </div>
    </div>
  </div>
);

// Element options for the filter dropdown (same as upload selector)
const ELEMENT_OPTIONS = [
  { value: 'jump_triple_axel', label: 'Triple Axel (3A)' },
  { value: 'jump_triple_lutz', label: 'Triple Lutz (3Lz)' },
  { value: 'jump_triple_flip', label: 'Triple Flip (3F)' },
  { value: 'jump_triple_loop', label: 'Triple Loop (3Lo)' },
  { value: 'jump_triple_salchow', label: 'Triple Salchow (3S)' },
  { value: 'jump_triple_toe_loop', label: 'Triple Toe Loop (3T)' },
  { value: 'jump_quad_axel', label: 'Quad Axel (4A)' },
  { value: 'jump_quad_lutz', label: 'Quad Lutz (4Lz)' },
  { value: 'jump_quad_flip', label: 'Quad Flip (4F)' },
  { value: 'jump_quad_loop', label: 'Quad Loop (4Lo)' },
  { value: 'jump_quad_salchow', label: 'Quad Salchow (4S)' },
  { value: 'jump_quad_toe_loop', label: 'Quad Toe Loop (4T)' },
  { value: 'combo_3f_3t', label: 'Combo: 3F+3T' },
  { value: 'jump_double_axel', label: 'Double Axel (2A)' },
  { value: 'jump_double_flip', label: 'Double Flip (2F)' },
  { value: 'jump_double_lutz', label: 'Double Lutz (2Lz)' },
  { value: 'jump_double_loop', label: 'Double Loop (2Lo)' },
  { value: 'jump_double_salchow', label: 'Double Salchow (2S)' },
  { value: 'jump_double_toe_loop', label: 'Double Toe Loop (2T)' },
  { value: 'jump_single_flip', label: 'Single Flip (1F)' },
  { value: 'jump_single_lutz', label: 'Single Lutz (1Lz)' },
  { value: 'jump_single_loop', label: 'Single Loop (1Lo)' },
  { value: 'jump_single_salchow', label: 'Single Salchow (1S)' },
  { value: 'jump_single_toe_loop', label: 'Single Toe Loop (1T)' },
  { value: 'spin_camel', label: 'Spin - Camel (CSp)' },
  { value: 'spin_sit', label: 'Spin - Sit (SSp)' },
  { value: 'spin_upright', label: 'Spin - Upright (USp)' },
  { value: 'spin_layback', label: 'Spin - Layback (LSp)' },
  { value: 'spin_combination', label: 'Spin - Combination (CoSp)' },
  { value: 'spin_change_foot_combination', label: 'Spin - CCoSp' },
  { value: 'spin_flying_sit', label: 'Spin - Flying Sit (FSSp)' },
];

const VideoHistory = ({ onVideoSelect, cache, setCache, compareMode = false, onCompareSelect, filterElementType = null }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isRevalidating, setIsRevalidating] = useState(false);

  // Use ref to track blob URLs for proper cleanup
  const blobUrlsRef = useRef({});
  const [thumbnailUrls, setThumbnailUrls] = useState({});

  // Infinite scroll refs
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Cleanup blob URLs
  const cleanupBlobUrls = useCallback(() => {
    Object.values(blobUrlsRef.current).forEach(url => {
      URL.revokeObjectURL(url);
    });
    blobUrlsRef.current = {};
  }, []);

  // Fetch a single thumbnail (fallback for items without thumbnail_data)
  const fetchThumbnail = useCallback(async (videoId) => {
    if (blobUrlsRef.current[videoId]) return;

    try {
      const response = await fetch(`${API_URL}/api/v1/thumbnail/${videoId}`, {
        headers: { ...authService.getAuthHeader() },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        blobUrlsRef.current[videoId] = url;
        setThumbnailUrls(prev => ({ ...prev, [videoId]: url }));
      }
    } catch (error) {
      console.error('Failed to fetch thumbnail:', error);
    }
  }, []);

  // Fetch history with pagination
  const fetchHistory = useCallback(async (reset = false, isBackground = false) => {
    const offset = reset ? 0 : currentOffset;

    if (reset && !isBackground) {
      setLoading(true);
    } else if (!reset) {
      setLoadingMore(true);
    }

    if (isBackground) {
      setIsRevalidating(true);
    }

    try {
      const response = await fetch(
        `${API_URL}/api/v1/video-history?limit=${PAGE_SIZE}&offset=${offset}`,
        { headers: { ...authService.getAuthHeader() } }
      );
      const data = await response.json();

      if (data.success) {
        const newHistory = data.history || [];

        if (reset) {
          if (!isBackground) {
            cleanupBlobUrls();
          }
          setHistory(newHistory);
          setCurrentOffset(PAGE_SIZE);
        } else {
          setHistory(prev => [...prev, ...newHistory]);
          setCurrentOffset(offset + PAGE_SIZE);
        }

        setTotalCount(data.total || data.count);
        setHasMore(newHistory.length === PAGE_SIZE);

        // Update cache
        if (setCache) {
          const updatedData = reset ? newHistory : [...history, ...newHistory];
          setCache({
            data: updatedData,
            timestamp: Date.now(),
            total: data.total || data.count
          });
        }

        // Fetch thumbnails only for items without base64 thumbnail_data
        newHistory.forEach(item => {
          if (!item.thumbnail_data && item.thumbnail_path && !blobUrlsRef.current[item.video_id]) {
            fetchThumbnail(item.video_id);
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setIsRevalidating(false);
    }
  }, [currentOffset, cleanupBlobUrls, fetchThumbnail, setCache, history]);

  // Initial load with stale-while-revalidate cache strategy
  useEffect(() => {
    if (cache?.data && cache?.timestamp) {
      const age = Date.now() - cache.timestamp;

      // Use cached data immediately
      setHistory(cache.data);
      setTotalCount(cache.total || cache.data.length);
      setHasMore(cache.data.length >= PAGE_SIZE);
      setCurrentOffset(cache.data.length);
      setLoading(false);

      // Fetch thumbnails for cached items
      cache.data.forEach(item => {
        if (!item.thumbnail_data && item.thumbnail_path && !blobUrlsRef.current[item.video_id]) {
          fetchThumbnail(item.video_id);
        }
      });

      // If cache is stale but not too old, revalidate in background
      if (age > CACHE_TTL && age < STALE_TTL) {
        fetchHistory(true, true); // Background revalidation
      } else if (age >= STALE_TTL) {
        // Cache too old, fetch fresh
        fetchHistory(true);
      }
      return;
    }

    // No cache - fetch fresh
    fetchHistory(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (loading) return;

    const handleObserver = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore) {
        fetchHistory(false);
      }
    };

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px', // Start loading 100px before reaching the end
      threshold: 0.1,
    });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, fetchHistory]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupBlobUrls();
  }, [cleanupBlobUrls]);

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this analysis?')) return;

    try {
      await fetch(`${API_URL}/api/v1/video-history/${videoId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeader() },
      });

      // Cleanup blob URL
      if (blobUrlsRef.current[videoId]) {
        URL.revokeObjectURL(blobUrlsRef.current[videoId]);
        delete blobUrlsRef.current[videoId];
        setThumbnailUrls(prev => {
          const updated = { ...prev };
          delete updated[videoId];
          return updated;
        });
      }

      // Remove from local state
      setHistory(prev => prev.filter(item => item.video_id !== videoId));
      setTotalCount(prev => prev - 1);

      // Update cache
      if (setCache && cache) {
        setCache({
          ...cache,
          data: cache.data.filter(item => item.video_id !== videoId),
          total: (cache.total || cache.data.length) - 1
        });
      }
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleRefresh = () => {
    fetchHistory(true);
  };

  const handleThumbnailActivate = useCallback((videoId) => {
    if (!compareMode && onVideoSelect) {
      onVideoSelect(videoId);
    }
  }, [compareMode, onVideoSelect]);

  // Render thumbnail - prioritize base64 data from response
  const renderThumbnail = (item) => {
    if (item.thumbnail_data) {
      return (
        <img
          src={`data:image/jpeg;base64,${item.thumbnail_data}`}
          alt="Thumbnail"
          loading="lazy"
        />
      );
    }

    if (thumbnailUrls[item.video_id]) {
      return (
        <img
          src={thumbnailUrls[item.video_id]}
          alt="Thumbnail"
          loading="lazy"
        />
      );
    }

    if (item.thumbnail_path) {
      return <div className="no-thumbnail">Loading...</div>;
    }

    return <div className="no-thumbnail">No thumbnail</div>;
  };

  // Initial loading state with skeletons
  if (loading) {
    return (
      <div className="video-history">
        <div className="history-header">
          <h4>Analysis History</h4>
        </div>
        <div className="history-grid">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Filter items by element type (compare mode filter OR self-contained filter)
  const activeCompareFilter = filterElementType && !showAllTypes;
  const activeLocalFilter = !compareMode && selectedFilter;
  const displayedHistory = activeCompareFilter
    ? history.filter(item => item.element_type === filterElementType)
    : activeLocalFilter
    ? history.filter(item => item.element_type === selectedFilter)
    : history;

  return (
    <div className="video-history">
      <div className="history-header">
        <h4>
          {activeCompareFilter ? (
            <>Matching Videos <span className="count">({displayedHistory.length})</span></>
          ) : (
            <>Analysis History {totalCount > 0 && <span className="count">({displayedHistory.length}{activeLocalFilter ? ` of ${totalCount}` : ''})</span>}</>
          )}
          {isRevalidating && (
            <span className="revalidating">
              <Loader size={14} className="spin" />
            </span>
          )}
        </h4>
        <div className="history-header-actions">
          {filterElementType && (
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={showAllTypes}
                onChange={(e) => setShowAllTypes(e.target.checked)}
              />
              Show all types
            </label>
          )}
          {!compareMode && (
            <select
              className="history-filter-select"
              value={selectedFilter || ''}
              onChange={(e) => setSelectedFilter(e.target.value || null)}
            >
              <option value="">All Types</option>
              {ELEMENT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
          <button className="refresh-btn" onClick={handleRefresh} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {displayedHistory.length === 0 ? (
        <p className="empty-state">{activeCompareFilter || activeLocalFilter ? 'No matching videos for this element type' : 'No previous analyses'}</p>
      ) : (
        <>
          <div className="history-grid">
            {displayedHistory.map((item) => (
              <div key={item.video_id} className="history-card">
                <div
                  className={`thumbnail ${!compareMode ? 'thumbnail-clickable' : ''}`}
                  onClick={!compareMode ? () => handleThumbnailActivate(item.video_id) : undefined}
                  onKeyDown={!compareMode ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleThumbnailActivate(item.video_id);
                    }
                  } : undefined}
                  role={!compareMode ? 'button' : undefined}
                  tabIndex={!compareMode ? 0 : undefined}
                  aria-label={!compareMode ? `View ${item.element_type.replace('_', ' ')}` : undefined}
                >
                  {renderThumbnail(item)}
                </div>
                <div className="history-info">
                  <div className="element-type">
                    {item.element_type.replace('_', ' ').toUpperCase()}
                  </div>
                  <div className="timestamp">
                    <Clock size={14} />
                    {new Date(item.created_at).toLocaleDateString()}
                  </div>
                  <div className="actions">
                    {compareMode && onCompareSelect ? (
                      <button
                        className="compare-select-btn"
                        onClick={() => onCompareSelect(
                          item.video_id,
                          item.element_type,
                          new Date(item.created_at).toLocaleDateString(),
                          item.thumbnail_data ? `data:image/jpeg;base64,${item.thumbnail_data}` : (thumbnailUrls[item.video_id] || null),
                          item.frame_count || 0
                        )}
                      >
                        <Play size={16} /> Compare
                      </button>
                    ) : (
                      <>
                        <button onClick={() => onVideoSelect(item.video_id)}>
                          <Play size={16} /> View
                        </button>
                        <button onClick={() => handleDelete(item.video_id)}>
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Skeleton cards while loading more */}
            {loadingMore && [...Array(3)].map((_, i) => (
              <SkeletonCard key={`loading-${i}`} />
            ))}
          </div>

          {/* Infinite scroll trigger element */}
          {hasMore && (
            <div ref={loadMoreRef} className="infinite-scroll-trigger">
              {loadingMore ? (
                <div className="loading-indicator">
                  <Loader size={20} className="spin" />
                  <span>Loading more...</span>
                </div>
              ) : (
                <div className="scroll-hint">
                  Scroll for more ({history.length} of {totalCount})
                </div>
              )}
            </div>
          )}

          {!hasMore && history.length > 0 && (
            <div className="end-of-list">
              All {totalCount} videos loaded
            </div>
          )}
        </>
      )}

      <style jsx>{styles}</style>
    </div>
  );
};

// Styles extracted for cleaner JSX
const styles = `
  .video-history {
    margin-top: 24px;
    padding: 20px;
    background: #f9fafb;
    border-radius: 8px;
    overflow: hidden;
    max-width: 100%;
  }
  .history-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  .video-history h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .count {
    font-weight: 400;
    color: #6b7280;
    font-size: 14px;
  }
  .revalidating {
    display: inline-flex;
    align-items: center;
  }
  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .refresh-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    color: #6b7280;
  }
  .history-header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .filter-toggle {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #6b7280;
    cursor: pointer;
    white-space: nowrap;
  }
  .filter-toggle input {
    cursor: pointer;
  }
  .history-filter-select {
    padding: 4px 8px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    color: #374151;
    background: white;
    cursor: pointer;
    max-width: 180px;
    min-height: 32px;
  }
  .history-filter-select:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }
  .refresh-btn:hover {
    background: #f3f4f6;
    color: #374151;
  }
  .empty-state {
    text-align: center;
    color: #6b7280;
    padding: 40px 20px;
  }
  .history-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
    gap: 16px;
  }
  .history-card {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    max-width: 100%;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .history-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  .thumbnail {
    width: 100%;
    height: 160px;
    background: #1a1a2e;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .thumbnail-clickable {
    cursor: pointer;
  }
  .thumbnail-clickable:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: -2px;
  }
  .thumbnail img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    pointer-events: none;
  }
  .no-thumbnail {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #9ca3af;
  }
  .history-info {
    padding: 12px;
  }
  .element-type {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 8px;
  }
  .timestamp {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 12px;
  }
  .actions {
    display: flex;
    gap: 8px;
  }
  .actions button {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background 0.2s;
  }
  .actions button:hover {
    background: #f9fafb;
  }
  .compare-select-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px;
    border: 1px solid #3b82f6;
    background: #eff6ff;
    color: #1e40af;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    transition: background 0.2s;
  }
  .compare-select-btn:hover {
    background: #3b82f6;
    color: white;
  }

  /* Skeleton Loading Styles */
  .skeleton-card {
    pointer-events: none;
  }
  .skeleton-thumbnail {
    position: relative;
    overflow: hidden;
  }
  .skeleton-shimmer {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      #e5e7eb 0%,
      #f3f4f6 50%,
      #e5e7eb 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
  }
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  .skeleton-text {
    background: #e5e7eb;
    border-radius: 4px;
    animation: pulse 1.5s ease-in-out infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  .skeleton-title {
    height: 16px;
    width: 70%;
    margin-bottom: 8px;
  }
  .skeleton-date {
    height: 12px;
    width: 50%;
    margin-bottom: 12px;
  }
  .skeleton-actions {
    display: flex;
    gap: 8px;
  }
  .skeleton-button {
    flex: 1;
    height: 32px;
    background: #e5e7eb;
    border-radius: 4px;
    animation: pulse 1.5s ease-in-out infinite;
  }

  /* Infinite Scroll Styles */
  .infinite-scroll-trigger {
    padding: 20px;
    text-align: center;
  }
  .loading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #6b7280;
  }
  .scroll-hint {
    color: #9ca3af;
    font-size: 14px;
  }
  .end-of-list {
    text-align: center;
    padding: 16px;
    color: #9ca3af;
    font-size: 14px;
    border-top: 1px solid #e5e7eb;
    margin-top: 16px;
  }

  /* ===== DARK THEME ===== */
  body[data-theme="dark"] .video-history {
    background: #1e293b;
  }
  body[data-theme="dark"] .video-history h4 {
    color: #f1f5f9;
  }
  body[data-theme="dark"] .count {
    color: #94a3b8;
  }
  body[data-theme="dark"] .refresh-btn {
    background: #334155;
    border-color: #475569;
    color: #94a3b8;
  }
  body[data-theme="dark"] .refresh-btn:hover {
    background: #475569;
    color: #f1f5f9;
  }
  body[data-theme="dark"] .filter-toggle {
    color: #94a3b8;
  }
  body[data-theme="dark"] .history-filter-select {
    background: #334155;
    border-color: #475569;
    color: #e2e8f0;
  }
  body[data-theme="dark"] .history-filter-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
  body[data-theme="dark"] .empty-state {
    color: #94a3b8;
  }
  body[data-theme="dark"] .history-card {
    background: #0f172a;
    border-color: #334155;
  }
  body[data-theme="dark"] .history-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }
  body[data-theme="dark"] .element-type {
    color: #f1f5f9;
  }
  body[data-theme="dark"] .timestamp {
    color: #64748b;
  }
  body[data-theme="dark"] .actions button {
    background: #1e293b;
    border-color: #475569;
    color: #cbd5e1;
  }
  body[data-theme="dark"] .actions button:hover {
    background: #334155;
  }
  body[data-theme="dark"] .compare-select-btn {
    background: rgba(59, 130, 246, 0.15);
    border-color: #3b82f6;
    color: #60a5fa;
  }
  body[data-theme="dark"] .compare-select-btn:hover {
    background: #3b82f6;
    color: white;
  }
  body[data-theme="dark"] .skeleton-shimmer {
    background: linear-gradient(90deg, #334155 0%, #475569 50%, #334155 100%);
    background-size: 200% 100%;
  }
  body[data-theme="dark"] .skeleton-text,
  body[data-theme="dark"] .skeleton-button {
    background: #334155;
  }
  body[data-theme="dark"] .loading-indicator {
    color: #94a3b8;
  }
  body[data-theme="dark"] .end-of-list {
    color: #64748b;
    border-top-color: #334155;
  }

  /* ===== GOLD THEME ===== */
  body[data-theme="gold"] .video-history {
    background: rgba(42, 42, 42, 0.9);
  }
  body[data-theme="gold"] .video-history h4 {
    color: #f4e4bc;
  }
  body[data-theme="gold"] .count {
    color: rgba(244, 228, 188, 0.6);
  }
  body[data-theme="gold"] .refresh-btn {
    background: rgba(42, 42, 42, 0.8);
    border-color: rgba(212, 175, 55, 0.3);
    color: rgba(244, 228, 188, 0.8);
  }
  body[data-theme="gold"] .refresh-btn:hover {
    background: rgba(212, 175, 55, 0.15);
    border-color: rgba(212, 175, 55, 0.5);
    color: #f4e4bc;
  }
  body[data-theme="gold"] .filter-toggle {
    color: rgba(244, 228, 188, 0.6);
  }
  body[data-theme="gold"] .history-filter-select {
    background: rgba(42, 42, 42, 0.8);
    border-color: rgba(212, 175, 55, 0.3);
    color: rgba(244, 228, 188, 0.8);
  }
  body[data-theme="gold"] .history-filter-select:focus {
    border-color: rgba(212, 175, 55, 0.6);
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.15);
  }
  body[data-theme="gold"] .empty-state {
    color: rgba(244, 228, 188, 0.6);
  }
  body[data-theme="gold"] .history-card {
    background: rgba(26, 26, 26, 0.8);
    border-color: rgba(212, 175, 55, 0.2);
  }
  body[data-theme="gold"] .history-card:hover {
    border-color: rgba(212, 175, 55, 0.4);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }
  body[data-theme="gold"] .element-type {
    color: #f4e4bc;
  }
  body[data-theme="gold"] .timestamp {
    color: rgba(244, 228, 188, 0.5);
  }
  body[data-theme="gold"] .actions button {
    background: rgba(42, 42, 42, 0.8);
    border-color: rgba(212, 175, 55, 0.3);
    color: rgba(244, 228, 188, 0.8);
  }
  body[data-theme="gold"] .actions button:hover {
    background: rgba(212, 175, 55, 0.15);
    border-color: rgba(212, 175, 55, 0.5);
  }
  body[data-theme="gold"] .compare-select-btn {
    background: rgba(212, 175, 55, 0.1);
    border-color: #D4AF37;
    color: #D4AF37;
  }
  body[data-theme="gold"] .compare-select-btn:hover {
    background: #D4AF37;
    color: #1a1a1a;
  }
  body[data-theme="gold"] .skeleton-shimmer {
    background: linear-gradient(90deg, rgba(42,42,42,0.8) 0%, rgba(62,62,62,0.8) 50%, rgba(42,42,42,0.8) 100%);
    background-size: 200% 100%;
  }
  body[data-theme="gold"] .skeleton-text,
  body[data-theme="gold"] .skeleton-button {
    background: rgba(42, 42, 42, 0.8);
  }
  body[data-theme="gold"] .loading-indicator {
    color: rgba(244, 228, 188, 0.6);
  }
  body[data-theme="gold"] .end-of-list {
    color: rgba(244, 228, 188, 0.5);
    border-top-color: rgba(212, 175, 55, 0.2);
  }
`;

export default VideoHistory;
